import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  Observable,
  catchError,
  finalize,
  firstValueFrom,
  map,
  of,
  shareReplay,
  tap,
  throwError,
  timeout,
} from 'rxjs';

import { environment } from '../../../environments/environment';
import { skipLoading } from '../interceptors/skip-loading.context';
import { LoginRequest, LoginResponse } from '../../shared/interfaces/auth.interface';
import { SessionUser } from '../../shared/interfaces/session-user.interface';
import { decodeJwt } from '../utils/decode-jwt';
import { UserStore } from './user-store.service';

const ACCESS_TOKEN_KEY = 'mayorap_access_token';
const REFRESH_TOKEN_KEY = 'mayorap_refresh_token';
/** Tope para el refresh al abrir la app: si la red no responde, se sigue igual. */
const STARTUP_REFRESH_TIMEOUT_MS = 8000;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly userStore = inject(UserStore);
  private readonly apiUrl = environment.apiUrl;
  private refreshInFlight$: Observable<string> | null = null;

  login(payload: LoginRequest): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${this.apiUrl}/auth/login`, payload, { context: skipLoading() })
      .pipe(
        tap((res) => {
          if (res.data.debeDefinirPassword) {
            this.logout();
            return;
          }
          const accessToken = res.data.accessToken;
          const refreshToken = res.data.refreshToken;
          if (!accessToken || !refreshToken) {
            return;
          }
          this.storeTokens({ accessToken, refreshToken });
          this.userStore.marcarVista(null);
          this.hydrateUser(accessToken);
        }),
      );
  }

  definirPassword(setupToken: string, password: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(
      `${this.apiUrl}/auth/definir-password`,
      { setupToken, password },
      { context: skipLoading() },
    );
  }

  logout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.userStore.clear();
  }

  /** Reemplaza la sesión actual. `vista` en null vuelve a la iglesia real del usuario. */
  reemplazarSesion(data: LoginResponse['data'], vista: string | null): void {
    if (!data.accessToken || !data.refreshToken) {
      return;
    }
    this.storeTokens({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    this.hydrateUser(data.accessToken);
    this.userStore.marcarVista(vista);
  }

  /**
   * Renueva la sesión con el refresh token (sesión deslizante).
   * Si ya hay un refresh en curso, todos comparten el mismo pedido.
   * Emite el nuevo access token.
   */
  refresh(): Observable<string> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      return throwError(
        () => new HttpErrorResponse({ status: 401, statusText: 'Sin refresh token' }),
      );
    }

    if (!this.refreshInFlight$) {
      this.refreshInFlight$ = this.http
        .post<LoginResponse>(
          `${this.apiUrl}/auth/refresh`,
          { refreshToken },
          { context: skipLoading() },
        )
        .pipe(
          map((res) => {
            const accessToken = res.data.accessToken;
            const newRefreshToken = res.data.refreshToken;
            if (!accessToken || !newRefreshToken) {
              throw new HttpErrorResponse({ status: 401, statusText: 'Refresh inválido' });
            }
            this.storeTokens({ accessToken, refreshToken: newRefreshToken });
            this.hydrateUser(accessToken);
            return accessToken;
          }),
          finalize(() => {
            this.refreshInFlight$ = null;
          }),
          shareReplay(1),
        );
    }

    return this.refreshInFlight$;
  }

  /** Al abrir la app renueva la sesión una vez. Nunca rechaza: no bloquea el arranque. */
  refreshOnStartup(): Promise<void> {
    if (!this.getRefreshToken()) {
      return Promise.resolve();
    }
    return firstValueFrom(
      this.refresh().pipe(
        timeout(STARTUP_REFRESH_TIMEOUT_MS),
        map(() => undefined),
        catchError((error: unknown) => {
          if (error instanceof HttpErrorResponse && error.status === 401) {
            this.logout();
          }
          return of(undefined);
        }),
      ),
    );
  }

  getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  private hydrateUser(token: string): void {
    const payload = decodeJwt(token);
    if (!payload) return;

    const session: SessionUser = {
      _id: payload.userId,
      iglesia: payload.iglesia,
      mailAccess: payload.mailAccess,
      admin: payload.roles.admin,
      lider: payload.roles.lider,
      contador: payload.roles.contador,
      tesorero: payload.roles.tesorero,
      musico: payload.roles.musico,
      liderAlabanza: payload.roles.liderAlabanza,
    };

    this.userStore.setUser(session);
  }

  private storeTokens(data: { accessToken: string; refreshToken: string }): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
  }
}
